import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { Button } from '../../components/buttons/Button';
import { SEOHead } from '../../components/seo/SEOHead';
import { Breadcrumb } from '../../components/seo/Breadcrumb';
import { Badge } from '../../components/common/Badge';
import { PropertyCard } from '../../components/cards/PropertyCard';
import { MapResultCard } from '../../components/cards/MapResultCard';
import { Search, MapPin, DollarSign, Home, Loader2, ListFilter, Calendar, X, ChevronDown, Navigation, Map as MapIcon, List, Check } from 'lucide-react';
import { GoogleGenAI } from "@google/genai";
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { generatePropertySlug } from '../../utils/seo/slugs';

// Fix Leaflet marker icons in React
const icon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

const AMENITIES_OPTIONS = [
  'Wifi', 'Parking', 'Security', 'Gym', 'Pool',
  'Furnished', 'AC', 'Study Desk', 'Laundry',
  'Backup Power', 'Garden', 'Cafeteria', 'Concierge'
];

// Haversine Formula for distance (returns km)
function getDistanceFromLatLonInKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  var R = 6371; // Radius of earth in km
  var dLat = deg2rad(lat2 - lat1);
  var dLon = deg2rad(lon2 - lon1);
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = R * c;
  return d;
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

// Component to auto-zoom map to fit markers
const MapBoundsFitter = ({ properties }: { properties: any[] }) => {
  const map = useMap();
  useEffect(() => {
    if (properties.length > 0) {
      const validCoords = properties
        .filter(p => p.latitude && p.longitude)
        .map(p => [p.latitude, p.longitude] as [number, number]);

      if (validCoords.length > 0) {
        const bounds = L.latLngBounds(validCoords);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      }
    }
  }, [properties, map]);
  return null;
}

export const BrowseAccommodation: React.FC = () => {
  const { properties } = useData();
  const [searchParams] = useSearchParams();

  // --- Filter State ---
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState('Any Price');
  const [roomType, setRoomType] = useState('All Types');
  const [availability, setAvailability] = useState(searchParams.get('status') === 'available' ? 'Available Now' : 'Any');
  const [sortBy, setSortBy] = useState('Recommended');

  // New Amenity Filter State
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [isAmenitiesOpen, setIsAmenitiesOpen] = useState(false);

  // --- View State ---
  const [showMap, setShowMap] = useState(false);

  // --- Geolocation State ---
  const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | null>(null);
  const [radius, setRadius] = useState<number>(5); // Default 5km
  const [isLocating, setIsLocating] = useState(false);

  // --- AI Grounding State ---
  const [aiResults, setAiResults] = useState<any[]>([]);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [useAiSearch, setUseAiSearch] = useState(false);

  // --- Handlers ---
  const handleGetUserLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setIsLocating(false);
        setSortBy('Distance'); // Auto-switch sort to distance
        setShowMap(true); // Auto-show map when location is found
      },
      (error) => {
        console.error("Geolocation Error:", error);
        alert("Unable to access location. Please check your browser permissions.");
        setIsLocating(false);
      }
    );
  };

  const clearLocation = () => {
    setUserLocation(null);
    setSortBy('Recommended');
  };

  const toggleAmenity = (amenity: string) => {
    setSelectedAmenities(prev =>
      prev.includes(amenity)
        ? prev.filter(a => a !== amenity)
        : [...prev, amenity]
    );
  };

  // --- Filtering Logic ---
  const getPriceRange = (range: string) => {
    if (range === 'R0 - R4000') return { min: 0, max: 4000 };
    if (range === 'R4000 - R8000') return { min: 4000, max: 8000 };
    if (range === 'R8000+') return { min: 8000, max: Infinity };
    return { min: 0, max: Infinity };
  };

  const filteredProperties = properties.filter(p => {
    // 1. Text Search
    const lowerTerm = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      p.title.toLowerCase().includes(lowerTerm) ||
      p.location.toLowerCase().includes(lowerTerm);

    // 2. Room Type & Price & Status
    const matchesType = roomType === 'All Types' || p.type === roomType;
    const { min, max } = getPriceRange(priceRange);
    const matchesPrice = p.price >= min && p.price <= max;
    const matchesAvailability = availability === 'Any' || availability === 'Any Availability' ||
      (availability === 'Available Now' && p.status === 'AVAILABLE') ||
      (availability === 'Available Soon' && p.status === 'PENDING') ||
      (availability === 'Rented' && p.status === 'RENTED');

    // 3. Geolocation Radius Filter
    let matchesRadius = true;
    if (userLocation && p.latitude && p.longitude) {
      const distance = getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, p.latitude, p.longitude);
      matchesRadius = distance <= radius;
    }

    // 4. Amenities Filter (AND Logic)
    // Property must contain ALL selected amenities
    const matchesAmenities = selectedAmenities.length === 0 ||
      selectedAmenities.every(a => (p.amenities || []).includes(a));

    return matchesSearch && matchesType && matchesPrice && matchesAvailability && matchesRadius && matchesAmenities;
  });

  // --- Sorting Logic ---
  const sortedProperties = [...filteredProperties].sort((a, b) => {
    if (sortBy === 'Price: Low to High') return a.price - b.price;
    if (sortBy === 'Price: High to Low') return b.price - a.price;

    if (sortBy === 'Distance' && userLocation) {
      const distA = (a.latitude && a.longitude) ? getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, a.latitude, a.longitude) : Infinity;
      const distB = (b.latitude && b.longitude) ? getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, b.latitude, b.longitude) : Infinity;
      return distA - distB;
    }

    return 0;
  });

  // --- AI Handlers ---
  const handleAiSearch = async () => {
    if (!searchTerm) return;
    setIsAiLoading(true);
    setAiResults([]);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      let prompt = `Find student accommodation in or near: "${searchTerm}".`;
      if (priceRange !== 'Any Price') prompt += ` ideally within ${priceRange} (ZAR).`;
      if (roomType !== 'All Types') prompt += ` suitable for ${roomType} living.`;

      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
        config: { tools: [{ googleMaps: {} }] },
      });

      const chunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
      const mapsChunks = chunks.filter((c: any) => c.maps);
      setAiResults(mapsChunks);
    } catch (error) {
      console.error("Gemini Search Error:", error);
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleSearch = () => {
    if (useAiSearch) handleAiSearch();
  };

  const handleReset = () => {
    setSearchTerm('');
    setPriceRange('Any Price');
    setRoomType('All Types');
    setAvailability('Any');
    setSortBy('Recommended');
    setSelectedAmenities([]);
    setAiResults([]);
    setUseAiSearch(false);
    clearLocation();
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex flex-col h-screen overflow-hidden">
      <SEOHead
        title="Browse Rooms for Rent | IndigoBeans Rooms"
        description="Browse our selection of verified rooms for rent. Filter by location, price, and amenities to find your perfect student accommodation or room share."
        canonical="https://rooms.indigobeans.co.za/browse"
      />
      {/* Breadcrumb Bar */}
      <div className="bg-white px-4 py-2 border-b">
        <Breadcrumb items={[{ name: 'Browse Rooms', url: '/browse' }]} />
      </div>

      {/* 2. Page Header & Filters (Scrollable part of layout if needed, but here fixed for app-feel) */}
      <div className="bg-white border-b border-gray-200 z-30 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Browse Accommodation</h1>
              <p className="text-sm text-gray-500 hidden sm:block">Find verified student homes near campus.</p>
            </div>
            {/* Map Toggle for Mobile & Desktop */}
            <Button variant="outline" className="lg:hidden" onClick={() => setShowMap(!showMap)}>
              {showMap ? <><List className="w-4 h-4 mr-2" /> List</> : <><MapIcon className="w-4 h-4 mr-2" /> Map</>}
            </Button>
            <Button variant="outline" className="hidden lg:flex" onClick={() => setShowMap(!showMap)}>
              {showMap ? <><List className="w-4 h-4 mr-2" /> Hide Map</> : <><MapIcon className="w-4 h-4 mr-2" /> Show Map</>}
            </Button>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-grow">
              <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="City, Zip, or University..."
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#2f5c96] outline-none transition-all text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>

            {/* Changed from overflow-x-auto to flex-wrap to prevent clipping of the Amenities dropdown */}
            <div className="flex gap-2 flex-wrap items-center">
              <select className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 min-w-[120px]" value={priceRange} onChange={(e) => setPriceRange(e.target.value)}>
                <option>Any Price</option>
                <option>R0 - R4000</option>
                <option>R4000 - R8000</option>
                <option>R8000+</option>
              </select>
              <select className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 min-w-[120px]" value={roomType} onChange={(e) => setRoomType(e.target.value)}>
                <option>All Types</option>
                <option>Studio</option>
                <option>Shared House</option>
                <option>Dorm</option>
              </select>
              <select className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 min-w-[120px]" value={availability} onChange={(e) => setAvailability(e.target.value)}>
                <option>Any Availability</option>
                <option>Available Now</option>
                <option>Available Soon</option>
                <option>Rented</option>
              </select>

              {/* Custom Amenities Multi-Select Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsAmenitiesOpen(!isAmenitiesOpen)}
                  className={`px-3 py-2 border rounded-lg text-sm flex items-center justify-between min-w-[130px] transition-colors ${selectedAmenities.length > 0 ? 'bg-blue-50 border-[#2f5c96] text-[#2f5c96] font-medium' : 'bg-gray-50 border-gray-200 text-gray-700'}`}
                >
                  <span className="truncate">
                    {selectedAmenities.length === 0 ? 'Amenities' : `${selectedAmenities.length} Selected`}
                  </span>
                  <ChevronDown className="w-4 h-4 ml-2 opacity-50" />
                </button>

                {isAmenitiesOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsAmenitiesOpen(false)}></div>
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-2 max-h-64 overflow-y-auto animate-scaleIn">
                      <div className="mb-2 px-2 py-1 flex justify-between items-center border-b border-gray-100">
                        <span className="text-xs font-bold text-gray-500 uppercase">Filter By</span>
                        {selectedAmenities.length > 0 && <button onClick={() => setSelectedAmenities([])} className="text-xs text-red-500 hover:underline">Clear</button>}
                      </div>
                      {AMENITIES_OPTIONS.map(opt => (
                        <div
                          key={opt}
                          onClick={() => toggleAmenity(opt)}
                          className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer select-none"
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center mr-2 transition-colors ${selectedAmenities.includes(opt) ? 'bg-[#2f5c96] border-[#2f5c96]' : 'border-gray-300 bg-white'}`}>
                            {selectedAmenities.includes(opt) && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <span className="text-sm text-gray-700">{opt}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <Button className="px-4 py-2" onClick={handleSearch} disabled={isAiLoading}>
                {isAiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </div>

          {/* Sub-bar: Count, Sort, Location, AI Toggle */}
          <div className="flex flex-wrap items-center justify-between mt-3 gap-2 text-sm">
            <div className="flex items-center gap-4 text-gray-600">
              <span className="font-medium">{aiResults.length > 0 ? aiResults.length : sortedProperties.length} results</span>

              {selectedAmenities.length > 0 && (
                <div className="hidden sm:flex flex-wrap gap-1">
                  {selectedAmenities.map(am => (
                    <span key={am} className="bg-blue-100 text-[#2f5c96] text-xs px-2 py-0.5 rounded-full flex items-center">
                      {am} <button onClick={() => toggleAmenity(am)} className="ml-1 hover:text-blue-800"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}

              {userLocation ? (
                <span className="flex items-center text-green-700 bg-green-50 px-2 py-0.5 rounded-full text-xs border border-green-200">
                  <MapPin className="w-3 h-3 mr-1" /> Near Me ({radius}km) <button onClick={clearLocation} className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>
                </span>
              ) : (
                <button onClick={handleGetUserLocation} className="flex items-center text-[#2f5c96] hover:underline" disabled={isLocating}>
                  {isLocating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Navigation className="w-3 h-3 mr-1" />} Use Location
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center cursor-pointer select-none gap-2">
                <span className={`text-xs ${useAiSearch ? 'text-[#2f5c96] font-bold' : 'text-gray-500'}`}>Smart Maps Search</span>
                <div className="relative">
                  <input type="checkbox" className="sr-only" checked={useAiSearch} onChange={(e) => setUseAiSearch(e.target.checked)} />
                  <div className={`block w-8 h-5 rounded-full transition-colors ${useAiSearch ? 'bg-[#2f5c96]' : 'bg-gray-300'}`}></div>
                  <div className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${useAiSearch ? 'transform translate-x-3' : ''}`}></div>
                </div>
              </label>

              <select className="bg-transparent font-medium text-gray-700 focus:outline-none cursor-pointer text-xs sm:text-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option>Recommended</option>
                <option>Price: Low to High</option>
                <option>Price: High to Low</option>
                {userLocation && <option>Distance</option>}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Main Content Area (Split View) */}
      <div className="flex-1 overflow-hidden flex relative">

        {/* Left Column: Listings Grid (Scrollable) */}
        <div className={`flex-1 overflow-y-auto bg-slate-50 p-4 sm:p-6 lg:p-8 transition-all duration-300 ${showMap ? 'hidden lg:block lg:w-3/5 xl:w-1/2' : 'w-full'}`}>
          {isAiLoading ? (
            <div className="py-32 text-center">
              <Loader2 className="w-12 h-12 text-[#2f5c96] animate-spin mx-auto mb-6" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Searching...</h3>
            </div>
          ) : (
            <>
              {aiResults.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge color="blue">Smart Search</Badge>
                    <span className="text-sm text-gray-500">Found via Google Maps grounding</span>
                  </div>
                  <div className={`grid gap-6 ${showMap ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
                    {aiResults.map((result, idx) => <MapResultCard key={idx} result={result} />)}
                  </div>
                </div>
              )}

              {(aiResults.length === 0 || !useAiSearch) && (
                sortedProperties.length > 0 ? (
                  <div className={`grid gap-6 ${showMap ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
                    {sortedProperties.map(p => (
                      <PropertyCard
                        key={p.id}
                        property={p}
                        distance={userLocation && p.latitude && p.longitude ? getDistanceFromLatLonInKm(userLocation.lat, userLocation.lng, p.latitude, p.longitude) : undefined}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-24 border-2 border-dashed border-gray-200 rounded-xl">
                    <ListFilter className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-gray-900 mb-2">No properties found</h3>
                    <p className="text-gray-500 mb-6">Try removing some amenity filters or adjusting your price range.</p>
                    <Button variant="outline" onClick={handleReset}>Clear Filters</Button>
                  </div>
                )
              )}
            </>
          )}
        </div>

        {/* Right Column: Map (Sticky/Fixed) */}
        {showMap && (
          <div className={`absolute inset-0 lg:static lg:block lg:w-2/5 xl:w-1/2 bg-gray-100 z-20 ${!showMap ? 'hidden' : ''}`}>
            <MapContainer
              center={userLocation ? [userLocation.lat, userLocation.lng] : [-26.2041, 28.0473]}
              zoom={11}
              scrollWheelZoom={true}
              className="h-full w-full"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapBoundsFitter properties={sortedProperties} />

              {/* User Location Marker */}
              {userLocation && (
                <Marker position={[userLocation.lat, userLocation.lng]} icon={icon} opacity={0.7}>
                  <Popup>You are here</Popup>
                </Marker>
              )}

              {/* Property Markers */}
              {sortedProperties.filter(p => p.latitude && p.longitude).map(p => (
                <Marker
                  key={p.id}
                  position={[p.latitude!, p.longitude!]}
                  icon={icon}
                >
                  <Popup className="w-64 p-0 overflow-hidden">
                    <div className="flex flex-col">
                      <div className="h-32 w-full relative">
                        <img src={p.image} className="w-full h-full object-cover rounded-t-lg" alt={p.title} />
                        <div className="absolute top-2 right-2 bg-white/90 px-2 py-0.5 rounded text-xs font-bold text-[#2f5c96]">
                          R{p.price}
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="font-bold text-gray-900 text-sm mb-1 line-clamp-1">{p.title}</h3>
                        <p className="text-xs text-gray-500 mb-2 truncate">{p.location}</p>
                        <Link to={`/room/${p.slug || generatePropertySlug(p.title, p.location, p.id)}`} className="block w-full text-center py-1.5 bg-[#2f5c96] text-white text-xs font-bold rounded hover:bg-[#c19cca]">
                          View Details
                        </Link>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            {/* Mobile Close Map Button */}
            <div className="absolute top-4 left-4 lg:hidden z-[400]">
              <Button size="sm" variant="white" onClick={() => setShowMap(false)}>
                <List className="w-4 h-4 mr-2" /> List View
              </Button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};